import React, { useState } from 'react';
import { Layers, Download, Info, BarChart2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика амортизации основного средства
 */
const DepreciationSchedule = ({ 
  schedule, 
  results, 
  formData,
  showFullSchedule, 
  setShowFullSchedule 
}) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первых пяти лет, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 5);

  // Функция для получения названия метода амортизации
  const getDepreciationMethodName = (method) => {
    const methodNames = {
      'straight_line': 'Линейный метод',
      'reducing_balance': 'Метод уменьшаемого остатка',
      'sum_of_years': 'По сумме чисел лет',
      'units_of_production': 'Пропорционально объему продукции'
    };
    return methodNames[method] || method;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          График амортизации ({schedule.length} {formatYearsLabel(schedule.length)})
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 5 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} ${formatYearsLabel(schedule.length)}`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        {formData.includeTaxCalculation && (
          <button
            onClick={() => setActiveTab('tax')}
            className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
              activeTab === 'tax'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            С налоговым учетом
          </button>
        )}
        {formData.depreciationMethod === 'units_of_production' && (
          <button
            onClick={() => setActiveTab('units')}
            className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
              activeTab === 'units'
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            Производственный метод
          </button>
        )}
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>
            {showFullSchedule 
              ? `Полный график амортизации актива "${results.assetName}" методом "${getDepreciationMethodName(results.depreciationMethod)}". Прокрутите таблицу вправо для просмотра всех данных.` 
              : `Показаны первые ${displaySchedule.length} ${formatYearsLabel(displaySchedule.length)} графика амортизации. Нажмите "Показать все ${schedule.length} ${formatYearsLabel(schedule.length)}" для просмотра полного графика.`
            }
          </p>
        </div>
      </div>

      {/* Визуализация амортизации */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <BarChart2 className="w-4 h-4 mr-1.5 text-indigo-500" />
          Визуализация амортизации по годам
        </h4>
        <div className="h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center">
          {schedule.map((item, index) => {
            // Вычисляем ширину полосы для текущего года (пропорционально амортизации)
            const widthPercent = (item.depreciation / Math.max(...schedule.map(s => s.depreciation))) * 100;
            
            return (
              <div 
                key={index}
                className="h-full flex flex-col justify-end items-center"
                style={{ 
                  width: `${100 / schedule.length}%`,
                  minWidth: '8px',
                  maxWidth: '60px'
                }}
              >
                <div 
                  className="w-3/4 bg-indigo-500 dark:bg-indigo-400 rounded-t transition-all"
                  style={{ 
                    height: `${widthPercent}%`,
                    minHeight: '4px' 
                  }}
                  title={`Год ${item.year}: ${formatCurrency(item.depreciation)}`}
                ></div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
          <span>Год 1</span>
          <span>Год {schedule.length}</span>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[500px] depreciation-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Год</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Начальная стоимость</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Амортизация за год</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Накопленная амортизация</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Конечная стоимость</th>
                </>
              )}

              {activeTab === 'tax' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Бухгалтерская амортизация</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Балансовая стоимость</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налоговая амортизация</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Налоговая стоимость</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Разница</th>
                </>
              )}

              {activeTab === 'units' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Начальная стоимость</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Произведено единиц</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">% от общего объема</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Амортизация за год</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Конечная стоимость</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item) => (
              <tr
                key={item.year}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.year}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{item.date}</td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.startingBookValue)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-indigo-600 dark:text-indigo-400">{formatCurrency(item.depreciation)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.accumulatedDepreciation)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.endingBookValue)}
                    </td>
                  </>
                )}

                {activeTab === 'tax' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-indigo-600 dark:text-indigo-400">{formatCurrency(item.depreciation)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.endingBookValue)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-blue-600 dark:text-blue-400">{formatCurrency(item.taxDepreciation)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.taxBookValue)}</td>
                    <td className={`px-3 py-1.5 whitespace-nowrap text-right font-medium ${
                      item.depreciation > item.taxDepreciation 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatCurrency(item.depreciation - item.taxDepreciation)}
                    </td>
                  </>
                )}

                {activeTab === 'units' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{formatCurrency(item.startingBookValue)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">{item.unitsProduced?.toLocaleString() || 0}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {formatNumber((item.unitsProduced / results.productionUnits) * 100, 2)}%
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-indigo-600 dark:text-indigo-400">{formatCurrency(item.depreciation)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.endingBookValue)}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.totalDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.totalDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.salvageValue)}</td>
                </>
              )}

              {activeTab === 'tax' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.totalDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.salvageValue)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.totalTaxDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.assetCost - results.totalTaxDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.accountingTaxDifference)}</td>
                </>
              )}

              {activeTab === 'units' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.assetCost)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{results.productionUnits.toLocaleString()}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">100%</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.totalDepreciation)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(results.salvageValue)}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 5 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} {formatYearsLabel(schedule.length)}
          </Button>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default DepreciationSchedule;